import React from "react";
import { Link } from "react-router-dom";
import { Logo, TAGLINE } from "../components/Logo";
import {
  GraduationCap,
  Users,
  ClipboardCheck,
  MessageSquare,
  Sparkles,
  Trophy,
  ArrowRight,
  Check,
  Star,
} from "lucide-react";

const FEATURES = [
  { icon: ClipboardCheck, title: "Smart Attendance", desc: "One-tap marking, auto-reports for parents and admins." },
  { icon: GraduationCap, title: "MCQ Tests & Auto-grading", desc: "Create timed tests, auto-evaluate, build leaderboards." },
  { icon: Sparkles, title: "AI Performance Insights", desc: "Claude-powered insights and instant report cards." },
  { icon: MessageSquare, title: "In-app Chat", desc: "Direct messaging between teacher, student & parent." },
  { icon: Trophy, title: "Gamified Leaderboard", desc: "Healthy competition that lifts every learner." },
  { icon: Users, title: "Role-based Dashboards", desc: "Admin, Teacher, Student, Parent — each with the right tools." },
];

const PLANS = [
  {
    id: "monthly_basic",
    name: "Monthly Basic",
    price: "₹199",
    cycle: "/ month",
    desc: "Perfect for individual students.",
    features: ["Attendance tracking", "MCQ tests", "Homework access", "AI insights (10/mo)"],
  },
  {
    id: "yearly_pro",
    name: "Yearly Pro",
    price: "₹1,999",
    cycle: "/ year",
    desc: "Best value for serious learners.",
    features: ["Everything in Basic", "Unlimited AI insights", "Priority support", "Advanced analytics"],
    highlight: true,
  },
  {
    id: "institute_white_label",
    name: "Institute White-Label",
    price: "₹4,999",
    cycle: "/ year",
    desc: "Run your own coaching brand on YoungMinds.",
    features: ["Multi-batch & multi-teacher", "Branded experience", "Bulk fee management", "Dedicated success manager"],
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* NAV */}
      <header className="sticky top-0 z-40 ym-glass border-b border-white/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-slate-600">
            <a href="#features" className="hover:text-blue-600">Features</a>
            <a href="#pricing" className="hover:text-blue-600">Pricing</a>
            <a href="#testimonials" className="hover:text-blue-600">Reviews</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link
              to="/login"
              data-testid="nav-login"
              className="px-4 py-2 text-sm font-semibold text-slate-700 hover:text-blue-600"
            >
              Login
            </Link>
            <Link
              to="/register"
              data-testid="nav-get-started"
              className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
            >
              Get started
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden ym-grid-bg">
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-blue-100/60 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-blue-50 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-bold uppercase tracking-wider">
                <Star className="h-3.5 w-3.5 fill-blue-500 text-blue-500" /> Built for coaching institutes
              </span>
              <h1 className="mt-5 font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05]">
                Smart Learning,
                <br />
                <span className="text-blue-600">Lifted Higher.</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
                Young Minds is the all-in-one SaaS platform for coaching institutes — attendance, tests, fees, AI insights and parent communication. <span className="italic font-semibold text-slate-800">"{TAGLINE}"</span>
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/register"
                  data-testid="hero-get-started"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition-all"
                >
                  Start free <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="#features"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-slate-200 text-slate-800 font-semibold hover:border-blue-200 hover:text-blue-600"
                >
                  See features
                </a>
              </div>
              <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
                <div><span className="text-2xl font-heading font-black text-slate-900">4</span> roles, one platform</div>
                <div className="h-8 w-px bg-slate-200" />
                <div><span className="text-2xl font-heading font-black text-slate-900">AI</span>-powered reports</div>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl shadow-blue-600/10 border border-white">
                <img
                  src="https://images.unsplash.com/photo-1565598621680-94ac0c22b148?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                  alt="Student learning with Young Minds"
                  className="w-full h-[460px] object-cover"
                />
              </div>
              {/* Floating stat card */}
              <div className="hidden md:flex absolute -bottom-6 -left-6 bg-white rounded-2xl border border-slate-200 shadow-xl p-4 items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-600 grid place-items-center text-white">
                  <Trophy className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Top performer</div>
                  <div className="font-heading font-bold text-slate-900">Avg score 96%</div>
                </div>
              </div>
              <div className="hidden md:flex absolute -top-6 -right-6 bg-white rounded-2xl border border-slate-200 shadow-xl p-4 items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500 grid place-items-center text-white">
                  <Check className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Attendance</div>
                  <div className="font-heading font-bold text-slate-900">98% present today</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Features</span>
            <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl text-slate-900">
              Everything an institute needs. <span className="text-blue-600">Nothing it doesn't.</span>
            </h2>
            <p className="mt-4 text-slate-600">A single platform that connects admins, teachers, students and parents.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="ym-card-hover p-7 rounded-2xl bg-white border border-slate-200"
              >
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 grid place-items-center mb-4">
                  <f.icon className="h-5 w-5" strokeWidth={2.2} />
                </div>
                <h3 className="font-heading font-bold text-lg text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Pricing</span>
            <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl text-slate-900">
              Plans that grow with your institute
            </h2>
            <p className="mt-4 text-slate-600">Start free, upgrade when you're ready. White-label option available.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {PLANS.map((p) => (
              <div
                key={p.id}
                data-testid={`plan-${p.id}`}
                className={`relative rounded-2xl p-7 border ${
                  p.highlight
                    ? "bg-blue-600 text-white border-blue-600 shadow-2xl shadow-blue-600/30 scale-[1.02]"
                    : "bg-white text-slate-900 border-slate-200 ym-card-hover"
                }`}
              >
                {p.highlight && (
                  <span className="absolute -top-3 right-6 px-3 py-1 rounded-full bg-yellow-400 text-slate-900 text-xs font-bold uppercase tracking-wider">
                    Most popular
                  </span>
                )}
                <h3 className="font-heading font-bold text-xl">{p.name}</h3>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-heading font-black text-4xl">{p.price}</span>
                  <span className={`text-sm ${p.highlight ? "text-blue-100" : "text-slate-500"}`}>{p.cycle}</span>
                </div>
                <p className={`mt-2 text-sm ${p.highlight ? "text-blue-100" : "text-slate-600"}`}>{p.desc}</p>
                <ul className="mt-6 space-y-3 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${p.highlight ? "text-yellow-300" : "text-blue-600"}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className={`mt-7 block text-center px-5 py-3 rounded-full font-semibold transition-colors ${
                    p.highlight
                      ? "bg-white text-blue-600 hover:bg-slate-100"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  Choose {p.name}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section id="testimonials" className="py-20 lg:py-28 bg-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 ym-grid-bg opacity-20" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <Star className="h-8 w-8 mx-auto fill-yellow-300 text-yellow-300" />
          <p className="mt-6 font-heading font-bold text-2xl sm:text-3xl leading-snug">
            "Within a month, parents started messaging us thanking the platform.
            Our attendance jumped 22%, and grades followed."
          </p>
          <div className="mt-6 text-blue-100 text-sm">
            <span className="font-semibold text-white">Priya Sharma</span> · Director, Excel Tutorials
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900">
            Ready to lift every learner?
          </h2>
          <p className="mt-4 text-slate-600 max-w-xl mx-auto">
            Join hundreds of coaching institutes already using Young Minds to power their teaching.
          </p>
          <Link
            to="/register"
            data-testid="cta-get-started"
            className="mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 shadow-xl shadow-blue-600/20"
          >
            Start free today <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 bg-slate-900 text-slate-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo inverted />
          <p className="text-xs text-slate-400">© {new Date().getFullYear()} Young Minds Smart Learning Platform. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
